<script setup lang="ts">
import { computed } from 'vue'
import { NSwitch, useMessage } from 'naive-ui'
import { useFormattedDate } from '../composables/useFormattedDate'
import type { CvVersion } from '../stores/cv_versions'

// The kill switch for the public CV page. Publishing needs an approved
// version to show anything; the panel says so instead of hiding the toggle,
// and names the approved version the page is showing when there is one.
const props = defineProps<{
  published: boolean
  hasApproved: boolean
  saving: boolean
  liveVersion?: CvVersion | null
}>()
const emit = defineEmits<{ toggle: [published: boolean] }>()

const message = useMessage()
const { formatAbsoluteDate } = useFormattedDate()

const publicUrl = computed(() => `${window.location.origin}/cv`)

const showing = computed(() => {
  const live = props.liveVersion
  if (!props.published || !live) return ''
  return live.approved_at
    ? `Showing v${live.id} · approved ${formatAbsoluteDate(live.approved_at)}`
    : `Showing v${live.id}`
})

async function copyUrl(): Promise<void> {
  try {
    await navigator.clipboard.writeText(publicUrl.value)
    message.success('Link copied')
  } catch {
    message.error('Could not copy the link')
  }
}
</script>

<template>
  <section
    class="flex flex-col gap-2.5 rounded-2xl border border-border-subtle bg-surface-raised p-3.5"
    aria-labelledby="cv-publish-heading"
    data-testid="cv-publish"
  >
    <div class="flex items-center gap-3">
      <h2 id="cv-publish-heading" class="m-0 flex-1 text-sm font-semibold text-ink">Public page</h2>
      <NSwitch
        :value="props.published"
        :loading="props.saving"
        :disabled="props.saving"
        aria-label="Publish the public CV page"
        data-testid="cv-publish-toggle"
        @update:value="(v: boolean) => emit('toggle', v)"
      />
    </div>
    <p class="m-0 text-xs text-ink-faint">
      One stable URL, always the latest approved version. Off returns a plain 404.
    </p>

    <p v-if="props.published && !props.hasApproved" class="m-0 text-xs text-attention" data-testid="cv-publish-warning">
      Published, but nothing is approved yet, so the page still shows 404. Approve a version to make it live.
    </p>

    <div v-if="props.published" class="flex flex-col gap-2.5" data-testid="cv-publish-url">
      <a :href="publicUrl" target="_blank" rel="noopener" class="font-mono text-xs break-all text-life">{{
        publicUrl
      }}</a>
      <span v-if="showing" class="text-[13px] text-ink-muted" data-testid="cv-publish-showing">{{ showing }}</span>
      <button
        type="button"
        class="min-h-11 rounded-[10px] border border-border-control bg-surface-hover text-ink hover:bg-surface-hover/70 sm:min-h-10"
        data-testid="cv-publish-copy"
        @click="copyUrl"
      >
        Copy link
      </button>
    </div>
  </section>
</template>
